import { z } from "zod";
import { chartFields } from "./chart-schema";
import { GUIDE_LIMIT, guideSchema, layoutGridSchema } from "./layout";
import { LINE_ENDS } from "./line";
import { imageMaskSchema } from "./masks";
import { PATH_BRUSHES, PATH_DATA_MAX_LENGTH } from "./path";

/**
 * Document format v1 (see docs/04-dokumentformat.md).
 * Flat element map + ordered id lists per page, units are CSS pixels.
 *
 * All additions after the first release are optional or have defaults, so
 * documents written by older versions stay valid without a migration.
 */
export const SCENE_SCHEMA_VERSION = 1;

const id = z.string().min(1).max(64);
export const colorSchema = z
  .string()
  .regex(
    /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/,
    "Farbe als Hex-Wert, z. B. #1a2b3c",
  );

export const gradientStopSchema = z.object({
  offset: z.number().min(0).max(1),
  color: colorSchema,
});

export const linearGradientSchema = z.object({
  type: z.literal("linear"),
  /** CSS semantics: 0 = bottom → top, 90 = left → right. */
  angle: z.number().default(90),
  stops: z.array(gradientStopSchema).min(2).max(16),
});

export const radialGradientSchema = z.object({
  type: z.literal("radial"),
  stops: z.array(gradientStopSchema).min(2).max(16),
});

export const gradientSchema = z.discriminatedUnion("type", [
  linearGradientSchema,
  radialGradientSchema,
]);

/** A fill: either a solid hex colour or a gradient. */
export const paintSchema = z.union([colorSchema, gradientSchema]);

export const strokeSchema = z.object({
  color: colorSchema,
  width: z.number().min(0).max(500),
  dash: z.enum(["solid", "dashed", "dotted"]).default("solid"),
});

export const shadowSchema = z.object({
  x: z.number().min(-500).max(500).default(0),
  y: z.number().min(-500).max(500).default(8),
  blur: z.number().min(0).max(500).default(24),
  color: colorSchema.default("#00000040"),
});

/** Key of a brand token (colour, text style), e.g. "primary" or "heading-1". */
export const brandTokenKeySchema = z
  .string()
  .regex(/^[a-z0-9][a-z0-9-]{0,39}$/, "Schlüssel aus Kleinbuchstaben, Ziffern und Bindestrichen");

export const BRAND_RADIUS_KEYS = ["none", "sm", "md", "lg", "full"] as const;
export type BrandRadiusKey = (typeof BRAND_RADIUS_KEYS)[number];

/**
 * Links element properties to tokens of the brand kit (docs/06-brand-kit.md).
 * The property itself always holds the last resolved value, so documents
 * render correctly without the kit and fall back when a token is deleted.
 */
export const brandBindingSchema = z.object({
  /** Solid fill of a shape, background of a QR code, header of a table → colour token. */
  fill: brandTokenKeySchema.optional(),
  /** Stroke colour of a shape or image, lines of a table → colour token. */
  stroke: brandTokenKeySchema.optional(),
  /** Text colour, modules of a QR code, colour of an icon → colour token. */
  color: brandTokenKeySchema.optional(),
  /** Text outline colour → colour token. */
  textStroke: brandTokenKeySchema.optional(),
  /** Typography (font, weight, size, line height, spacing, case) → text style. */
  text: brandTokenKeySchema.optional(),
  /** Corner radius of a rectangle or image → radius token. */
  radius: z.enum(BRAND_RADIUS_KEYS).optional(),
});

// ---------------------------------------------------------------------------
// Time & animation (video documents, docs/05-video-und-untertitel.md).
// All times are milliseconds on the video's timeline.
// ---------------------------------------------------------------------------

const ms = z
  .number()
  .min(0)
  .max(24 * 60 * 60 * 1000);

/** When an element is visible in a video (end exclusive). Without it: the whole video. */
export const timeRangeSchema = z
  .object({ start: ms, end: ms })
  .refine((range) => range.end > range.start, "Das Ende muss nach dem Anfang liegen");

export const ANIMATION_PRESETS = [
  "fade",
  "slide-up",
  "slide-down",
  "slide-left",
  "slide-right",
  "pop",
  "zoom",
  "blur",
  "typewriter",
  "fly-left",
  "fly-right",
  "fly-up",
  "fly-down",
  "drop",
  "spin",
] as const;
export type AnimationPreset = (typeof ANIMATION_PRESETS)[number];

export const LOOP_ANIMATIONS = ["pulse", "float", "wiggle"] as const;
export type LoopAnimation = (typeof LOOP_ANIMATIONS)[number];

export const animationStepSchema = z.object({
  preset: z.enum(ANIMATION_PRESETS),
  duration: z.number().min(50).max(10_000).default(500),
});

/** Entrance, exit and a looping emphasis while visible. Pure functions of time in the renderer. */
export const elementAnimationSchema = z.object({
  in: animationStepSchema.optional(),
  out: animationStepSchema.optional(),
  loop: z.enum(LOOP_ANIMATIONS).optional(),
});

/** Most tracks above the main track (video/tracks.ts). */
export const MAX_TRACKS = 60;

const elementBase = {
  id,
  name: z.string().max(200).optional(),
  x: z.number(),
  y: z.number(),
  width: z.number().positive(),
  height: z.number().positive(),
  /** Degrees, clockwise, around the element centre. */
  rotation: z.number().default(0),
  opacity: z.number().min(0).max(1).default(1),
  locked: z.boolean().default(false),
  hidden: z.boolean().default(false),
  shadow: shadowSchema.optional(),
  brand: brandBindingSchema.optional(),
  /** Video only: visible time range. */
  time: timeRangeSchema.optional(),
  /** Video only: entrance/exit animation. */
  animation: elementAnimationSchema.optional(),
  /**
   * Video only: drawn between the background and the cut-out person of the
   * clips with `cutout` (video/cutout.ts) – a title behind the speaker.
   * Only on top-level elements.
   */
  behindCutout: z.boolean().optional(),
  /**
   * Video only: the track the element lies on (video/tracks.ts), shared with
   * the clips of the upper video tracks – higher tracks lie on top. Only on
   * top-level elements; without it the element lies above everything.
   */
  track: z.number().int().min(1).max(MAX_TRACKS).optional(),
};

export const textStyleSchema = z.object({
  fontFamily: z.string().max(100).default("Inter"),
  fontSize: z.number().positive().max(2000).default(64),
  fontWeight: z.number().int().min(100).max(900).default(700),
  italic: z.boolean().default(false),
  underline: z.boolean().default(false),
  color: colorSchema.default("#111111"),
  align: z.enum(["left", "center", "right", "justify"]).default("left"),
  verticalAlign: z.enum(["top", "middle", "bottom"]).default("top"),
  lineHeight: z.number().positive().max(5).default(1.15),
  /** In em. */
  letterSpacing: z.number().min(-1).max(2).default(0),
  uppercase: z.boolean().default(false),
  /** Outline around the glyphs. */
  stroke: z.object({ color: colorSchema, width: z.number().min(0).max(50) }).optional(),
  /** Line through the text. */
  strike: z.boolean().optional(),
});

/**
 * Text effects besides shadow (`shadow`) and outline (`style.stroke`),
 * scene/text-effects.ts. Amounts are 0..100, relative to the font size.
 */
const effectAmount = z.number().min(0).max(100);
export const TEXT_EFFECT_TYPES = [
  "lift",
  "hollow",
  "neon",
  "echo",
  "glitch",
  "highlight",
  "gradient",
] as const;
export type TextEffectType = (typeof TEXT_EFFECT_TYPES)[number];
export const textEffectSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("lift"), intensity: effectAmount.default(50) }),
  z.object({ type: z.literal("hollow"), thickness: effectAmount.default(30) }),
  z.object({ type: z.literal("neon"), intensity: effectAmount.default(50) }),
  z.object({
    type: z.literal("echo"),
    offset: effectAmount.default(30),
    /** Degrees, 0 = right, 90 = down. */
    direction: z.number().min(-180).max(180).default(45),
    /** Without: the text colour. */
    color: colorSchema.optional(),
  }),
  z.object({
    type: z.literal("glitch"),
    offset: effectAmount.default(30),
    direction: z.number().min(-180).max(180).default(0),
    colors: z.tuple([colorSchema, colorSchema]).default(["#00ffff", "#ff00ff"]),
  }),
  z.object({
    type: z.literal("highlight"),
    color: colorSchema.default("#ffe066"),
    /** Space around the text. */
    padding: effectAmount.default(30),
    radius: effectAmount.default(30),
    opacity: z.number().min(0).max(1).default(1),
  }),
  z.object({ type: z.literal("gradient"), fill: gradientSchema }),
]);

/** Longest text of a text element. */
export const TEXT_MAX_LENGTH = 5000;

/**
 * Formatting of a part of the text (`from` inclusive, `to` exclusive, UTF-16
 * offsets into `text`). Set fields override the element's style; underline and
 * line-through can only be added (see scene/richtext.ts).
 */
export const textMarkSchema = z
  .object({
    from: z.number().int().min(0).max(TEXT_MAX_LENGTH),
    to: z.number().int().min(1).max(TEXT_MAX_LENGTH),
    fontFamily: z.string().max(100).optional(),
    fontSize: z.number().positive().max(2000).optional(),
    fontWeight: z.number().int().min(100).max(900).optional(),
    italic: z.boolean().optional(),
    underline: z.boolean().optional(),
    strike: z.boolean().optional(),
    color: colorSchema.optional(),
    /** Brand colour the `color` comes from (follows the kit like `brand.color`). */
    colorToken: brandTokenKeySchema.optional(),
  })
  .refine((mark) => mark.to > mark.from, "Leerer Textbereich");

export const TEXT_LIST_KINDS = ["bullet", "number"] as const;
export type TextListKind = (typeof TEXT_LIST_KINDS)[number];

/** Format of one paragraph (a line of `text` between line breaks). */
export const paragraphFormatSchema = z.object({
  list: z.enum(TEXT_LIST_KINDS).optional(),
  /** Nesting level of list items. */
  indent: z.number().int().min(0).max(4).optional(),
});

export const textElementSchema = z.object({
  ...elementBase,
  type: z.literal("text"),
  text: z.string().max(TEXT_MAX_LENGTH),
  /** Mixed formatting, sorted and without overlaps. */
  marks: z.array(textMarkSchema).max(2000).optional(),
  /** Paragraph formats by paragraph index (null: plain paragraph). */
  paragraphs: z.array(paragraphFormatSchema.nullable()).max(TEXT_MAX_LENGTH).optional(),
  style: textStyleSchema.default(textStyleSchema.parse({})),
  /** The editor keeps `height` in sync with the text content. */
  autoHeight: z.boolean().default(true),
  /**
   * Font size relative to the linked text style (`brand.text`), e.g. a template
   * headline at 1.6 × "heading-1". Ignored without that link.
   */
  textScale: z.number().min(0.1).max(10).optional(),
  /** Text effect besides shadow and outline (scene/text-effects.ts). */
  effect: textEffectSchema.optional(),
  /** Curved text along a circle, −100..100 % (positive: arch up). */
  curve: z.number().min(-100).max(100).optional(),
});

/** Visible part of the source image, relative to its natural size (0..1). */
export const cropSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  width: z.number().positive().max(1),
  height: z.number().positive().max(1),
});

/**
 * Colour correction of a clip or an image element (docs/05-video-und-untertitel.md).
 * The sliders form one colour matrix (`colorMatrix`), which the editor
 * preview (SVG filter), the image exports and the video export (ffmpeg
 * colorchannelmixer) apply identically; the vignette darkens the corners of
 * the clip's area or the image's box.
 */
export const clipColorSchema = z.object({
  /** Id of the look the values came from (built-in or saved), for the gallery. */
  look: z.string().max(64).optional(),
  brightness: z.number().min(-1).max(1).default(0),
  contrast: z.number().min(-1).max(1).default(0),
  saturation: z.number().min(-1).max(1).default(0),
  /** Negative = cooler (blue), positive = warmer (orange). */
  temperature: z.number().min(-1).max(1).default(0),
  /** Negative = green, positive = magenta. */
  tint: z.number().min(-1).max(1).default(0),
  /** Lifted blacks (faded film). */
  fade: z.number().min(0).max(1).default(0),
  /** Darkened corners. */
  vignette: z.number().min(0).max(1).default(0),
  /** Duotone: brightness mapped from `dark` (shadows) to `light` (highlights), last step. */
  duotone: z.object({ dark: colorSchema, light: colorSchema }).optional(),
});

export const imageElementSchema = z.object({
  ...elementBase,
  type: z.literal("image"),
  assetId: z.uuid(),
  /** Used when no explicit `crop` is set. */
  fit: z.enum(["cover", "contain"]).default("cover"),
  crop: cropSchema.nullable().default(null),
  radius: z.number().min(0).default(0),
  flipX: z.boolean().default(false),
  flipY: z.boolean().default(false),
  stroke: strokeSchema.optional(),
  /** Adjustments and look ("Bildanpassungen"), like the colour of video clips. */
  color: clipColorSchema.optional(),
  /** Form the image is clipped to ("Bildform", scene/masks.ts); none = box with `radius`. */
  mask: imageMaskSchema.optional(),
});

export const SHAPE_KINDS = ["rect", "ellipse", "triangle", "line", "polygon", "star"] as const;
export type ShapeKind = (typeof SHAPE_KINDS)[number];

export const PATTERN_KINDS = [
  "dots",
  "stripes",
  "grid",
  "waves",
  "zigzag",
  "circles",
  "triangles",
  "confetti",
] as const;
export type PatternKind = (typeof PATTERN_KINDS)[number];

/** Vector pattern drawn over a shape's fill (scene/patterns.ts). */
export const shapePatternSchema = z.object({
  kind: z.enum(PATTERN_KINDS),
  color: colorSchema,
  /** Size of one tile in px. */
  size: z.number().min(4).max(1000).default(60),
  /** Degrees around the shape's centre. */
  rotation: z.number().min(-360).max(360).default(0),
  opacity: z.number().min(0).max(1).default(1),
});

export const shapeElementSchema = z.object({
  ...elementBase,
  type: z.literal("shape"),
  shape: z.enum(SHAPE_KINDS),
  fill: paintSchema.default("#dddddd"),
  stroke: strokeSchema.optional(),
  /** Corner radius for `rect`. */
  radius: z.number().min(0).default(0),
  /** Corners of `polygon` / points of `star`. */
  sides: z.number().int().min(3).max(24).default(5),
  /** Inner radius of `star` relative to the outer radius. */
  innerRatio: z.number().min(0.1).max(0.95).default(0.5),
  /** Image placeholder (templates): an image dropped onto it takes its place and shape. */
  placeholder: z.literal("image").optional(),
  /** `line` only: caps at the start (left) and end (right), e.g. arrowheads (scene/line.ts). */
  lineStart: z.enum(LINE_ENDS).optional(),
  lineEnd: z.enum(LINE_ENDS).optional(),
  /** `line` only: round line ends and joins. */
  lineRound: z.boolean().optional(),
  /** `line` only: bend relative to the length (0 straight, positive bulges upwards). */
  curve: z.number().min(-1).max(1).optional(),
  /** Free outline instead of `shape` (frames: heart, arch, blob …; not for lines), see scene/masks.ts. */
  mask: imageMaskSchema.optional(),
  /** Pattern over the fill (rectangles and ellipses). */
  pattern: shapePatternSchema.optional(),
});

/** Vector graphic from the media library. */
export const svgElementSchema = z.object({
  ...elementBase,
  type: z.literal("svg"),
  assetId: z.uuid(),
  flipX: z.boolean().default(false),
  flipY: z.boolean().default(false),
});

export const QR_ECC_LEVELS = ["L", "M", "Q", "H"] as const;
export type QrEcc = (typeof QR_ECC_LEVELS)[number];

/** Look of the modules; the three finder patterns stay solid squares (or rounded). */
export const QR_STYLES = ["square", "rounded", "dots"] as const;
export type QrStyle = (typeof QR_STYLES)[number];

/** Longest text a QR code element encodes (fits version 40 at every correction level). */
export const QR_MAX_LENGTH = 1000;

/** Largest logo side relative to the code (without quiet zone). */
export const QR_LOGO_MAX_SIZE = 0.3;
export const QR_LOGO_MIN_SIZE = 0.1;

/**
 * Logo in the middle of a QR code (image or graphic of the media library).
 * The error correction makes up for the covered modules (see `qrProblems`).
 */
export const qrLogoSchema = z.object({
  assetId: z.uuid(),
  /** Side of the logo area relative to the code (without quiet zone). */
  size: z.number().min(QR_LOGO_MIN_SIZE).max(QR_LOGO_MAX_SIZE).default(0.22),
  /** Leave out the modules under the logo (a clean area in the background colour). */
  clear: z.boolean().default(true),
});

/**
 * QR code, drawn as vector modules (sharp in print). The code is square and
 * sits centred in the box. `value` is the encoded text: a link, `mailto:`,
 * `tel:`, `WIFI:` or a vCard (see `scene/qr.ts`).
 */
export const qrElementSchema = z.object({
  ...elementBase,
  type: z.literal("qr"),
  value: z.string().min(1).max(QR_MAX_LENGTH),
  /** Colour of the modules. */
  color: colorSchema.default("#111111"),
  /** Background including the quiet zone (transparent: `#ffffff00`). */
  background: colorSchema.default("#ffffff"),
  /** Error correction: higher levels survive more damage but need more modules. */
  ecc: z.enum(QR_ECC_LEVELS).default("M"),
  /** Quiet zone around the code, in modules. */
  margin: z.number().int().min(0).max(10).default(2),
  style: z.enum(QR_STYLES).default("square"),
  logo: qrLogoSchema.optional(),
});

export const TABLE_MAX_ROWS = 60;
export const TABLE_MAX_COLUMNS = 12;
export const TABLE_CELL_MAX_LENGTH = 500;

export const tableColumnSchema = z.object({
  /** Relative width (the columns share the box width in proportion). */
  width: z.number().positive().max(100).default(1),
  align: z.enum(["left", "center", "right"]).default("left"),
});

/**
 * Table (see `scene/table.ts`): rows of plain-text cells, drawn as an HTML
 * table in the box width (vector in print). The height follows the content;
 * `rowHeight` is the minimum height of a row.
 */
export const tableElementSchema = z.object({
  ...elementBase,
  type: z.literal("table"),
  /** Rows of cells; rows shorter than `columns` are padded with empty cells. */
  cells: z
    .array(z.array(z.string().max(TABLE_CELL_MAX_LENGTH)).max(TABLE_MAX_COLUMNS))
    .min(1)
    .max(TABLE_MAX_ROWS),
  columns: z.array(tableColumnSchema).min(1).max(TABLE_MAX_COLUMNS),
  /** First row as header (bold, header fill). */
  header: z.boolean().default(true),
  /** Every second body row with `stripeFill`. */
  zebra: z.boolean().default(false),
  fontFamily: z.string().max(100).default("Inter"),
  fontSize: z.number().positive().max(500).default(28),
  fontWeight: z.number().int().min(100).max(900).default(400),
  lineHeight: z.number().positive().max(5).default(1.3),
  /** Text colour of the body. */
  color: colorSchema.default("#111111"),
  headerColor: colorSchema.default("#ffffff"),
  headerFill: colorSchema.default("#111827"),
  bodyFill: colorSchema.default("#ffffff00"),
  stripeFill: colorSchema.default("#f4f4f5"),
  borderColor: colorSchema.default("#d4d4d8"),
  borderWidth: z.number().min(0).max(50).default(2),
  /** Inner spacing of the cells in px. */
  padding: z.number().min(0).max(200).default(16),
  /** Minimum height of a row in px. */
  rowHeight: z.number().min(0).max(2000).default(0),
});

/** Styles of the built-in icons (Phosphor), in the order of the style switch. */
export const ICON_WEIGHTS = ["regular", "bold", "fill", "duotone", "light", "thin"] as const;
export type IconWeight = (typeof ICON_WEIGHTS)[number];

/** Icons are drawn on a 256 × 256 grid. */
export const ICON_VIEWBOX = 256;
/** Longest path data of one icon layer (Phosphor's longest icon has about 2 500 characters). */
export const ICON_PATH_MAX_LENGTH = 10_000;
export const ICON_MAX_PATHS = 8;

/**
 * SVG path data: path commands, numbers and separators only. The renderer
 * passes it to `<path d>` as a React prop, never as markup.
 */
export const iconPathDataSchema = z
  .string()
  .min(2)
  .max(ICON_PATH_MAX_LENGTH)
  .regex(/^\s*[Mm][MmZzLlHhVvCcSsQqTtAa0-9eE+\-.,\s]*$/, "Ungültige Pfaddaten");

export const iconPathSchema = z.object({
  d: iconPathDataSchema,
  /** Layer opacity (the lighter layer of duotone icons: 0.2). */
  opacity: z.number().min(0).max(1).optional(),
});

/**
 * Icon from the built-in library (docs/04-dokumentformat.md). The element
 * keeps its own geometry (`paths`), so it renders without the catalog; `icon`
 * and `weight` only tell the editor which icon it is (style switch, swap).
 * The icon keeps its aspect ratio and sits centred in the box.
 */
export const iconElementSchema = z.object({
  ...elementBase,
  type: z.literal("icon"),
  /** Name in the catalog, e.g. "heart" or "shopping-cart". */
  icon: z
    .string()
    .max(64)
    .regex(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      "Icon-Name aus Kleinbuchstaben, Ziffern und Bindestrichen",
    ),
  weight: z.enum(ICON_WEIGHTS).default("regular"),
  paths: z.array(iconPathSchema).min(1).max(ICON_MAX_PATHS),
  color: colorSchema.default("#1a1a1a"),
  flipX: z.boolean().default(false),
  flipY: z.boolean().default(false),
});

/**
 * Device mockup (docs/19-mockups.md): a phone, tablet, laptop, screen or
 * browser window from the catalog (`devices/catalog.ts`) with a screenshot on
 * its screen. The device keeps its aspect ratio and sits centred in the box;
 * `tilt` turns it in 3D (perspective views).
 */
export const deviceTiltSchema = z.object({
  x: z.number().min(-75).max(75).default(0),
  y: z.number().min(-75).max(75).default(0),
  z: z.number().min(-180).max(180).default(0),
});

/** Model id in the catalog, e.g. "iphone-17-pro" (unknown ids draw the default phone). */
export const deviceIdSchema = z
  .string()
  .max(64)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Geräte-ID aus Kleinbuchstaben, Ziffern und Bindestrichen");

/** Colour of the device (id of one of its finishes). */
export const deviceFinishSchema = z.string().max(40);

/** Browser windows: address shown in the address bar. */
export const deviceUrlSchema = z.string().max(200);

export const deviceElementSchema = z.object({
  ...elementBase,
  type: z.literal("device"),
  device: deviceIdSchema,
  finish: deviceFinishSchema.optional(),
  /** Phones and tablets turned sideways. */
  landscape: z.boolean().default(false),
  tilt: deviceTiltSchema.default({ x: 0, y: 0, z: 0 }),
  /** Screenshot on the screen (image from the media library); without it the screen stays dark. */
  assetId: z.uuid().optional(),
  /** `cover` fills the screen, `contain` shows the whole picture. */
  fit: z.enum(["cover", "contain"]).default("cover"),
  /** Soft reflection on the glass. */
  glare: z.boolean().default(false),
  url: deviceUrlSchema.optional(),
});

/**
 * Freehand vector stroke (drawing tool, scene/path.ts). `d` is in the
 * coordinates of `viewBox`, which is stretched onto the box, so the stroke
 * (including its width) scales with the element.
 */
export const pathElementSchema = z.object({
  ...elementBase,
  type: z.literal("path"),
  d: z
    .string()
    .min(2)
    .max(PATH_DATA_MAX_LENGTH)
    .regex(/^\s*[Mm][MmZzLlHhVvCcSsQqTtAa0-9eE+\-.,\s]*$/, "Ungültige Pfaddaten"),
  viewBox: z.tuple([z.number().positive(), z.number().positive()]),
  /** Stroke colour. */
  color: colorSchema.default("#1a1a1a"),
  /** Stroke width in viewBox units. */
  strokeWidth: z.number().min(0).max(500).default(4),
  /** Fill of closed strokes (none when unset). */
  fill: colorSchema.optional(),
  /** Brush the stroke was drawn with; the highlighter multiplies with what lies below. */
  brush: z.enum(PATH_BRUSHES).default("pen"),
});

/**
 * Group of elements. Children are stored in `elements` like everything else;
 * their coordinates are relative to the group's top-left corner.
 */
/** Chart (bars, columns, lines, areas, pie, donut, progress ring), see scene/chart.ts. */
export const chartElementSchema = z.object({
  ...elementBase,
  type: z.literal("chart"),
  ...chartFields,
});

export const groupElementSchema = z.object({
  ...elementBase,
  type: z.literal("group"),
  children: z.array(id).min(1),
});

// ---------------------------------------------------------------------------
// Captions (automatic subtitles from the transcripts of the main track)
// ---------------------------------------------------------------------------

/**
 * How caption pages and words appear:
 * - `none` page switches without animation, `fade` / `slide-up` animate the whole page
 * - `pop` words appear when spoken with a slight overshoot
 * - `word-by-word` only the word being spoken is visible
 * - `typewriter` letters appear in sync with the speech
 * - `karaoke` upcoming words are dimmed, spoken words fully visible
 * - `bounce` the active word hops slightly
 */
export const CAPTION_ANIMATIONS = [
  "none",
  "fade",
  "slide-up",
  "pop",
  "word-by-word",
  "typewriter",
  "karaoke",
  "bounce",
] as const;
export type CaptionAnimation = (typeof CAPTION_ANIMATIONS)[number];

/** Emphasis of the word being spoken. */
export const CAPTION_HIGHLIGHTS = ["none", "color", "box", "underline"] as const;
export type CaptionHighlight = (typeof CAPTION_HIGHLIGHTS)[number];

export const captionStyleSchema = z.object({
  fontFamily: z.string().max(100).default("Inter"),
  fontWeight: z.number().int().min(100).max(900).default(800),
  italic: z.boolean().default(false),
  /** px on the document. */
  fontSize: z.number().positive().max(500).default(76),
  lineHeight: z.number().positive().max(3).default(1.15),
  /** In em. */
  letterSpacing: z.number().min(-0.5).max(1).default(0),
  uppercase: z.boolean().default(false),
  color: colorSchema.default("#ffffff"),
  /** Outline around the glyphs (width in px). */
  stroke: z.object({ color: colorSchema, width: z.number().min(0).max(40) }).optional(),
  shadow: shadowSchema.optional(),
  /** Box behind each line, each word or the whole caption. */
  background: z.enum(["none", "line", "word", "page"]).default("none"),
  backgroundColor: colorSchema.default("#000000b3"),
  /** px on the document. */
  backgroundRadius: z.number().min(0).max(200).default(16),
  highlight: z.enum(CAPTION_HIGHLIGHTS).default("color"),
  highlightColor: colorSchema.default("#facc15"),
  /** Text colour on the highlight box (`highlight: "box"`). */
  highlightTextColor: colorSchema.default("#111111"),
  /** Scale of the word being spoken. */
  activeScale: z.number().min(1).max(1.6).default(1),
  /** Opacity of upcoming words (karaoke). */
  upcomingOpacity: z.number().min(0).max(1).default(0.5),
  /** Emphasised keywords: colour (unset: the highlight colour), scale and bold. */
  emphasisColor: colorSchema.optional(),
  emphasisScale: z.number().min(1).max(1.6).optional(),
  emphasisBold: z.boolean().optional(),
  align: z.enum(["left", "center", "right"]).default("center"),
  verticalAlign: z.enum(["top", "middle", "bottom"]).default("middle"),
});

export const captionLayoutSchema = z.object({
  maxWordsPerLine: z.number().int().min(1).max(12).default(4),
  maxLines: z.number().int().min(1).max(4).default(2),
  maxCharsPerLine: z.number().int().min(4).max(80).default(22),
  /** A speech pause at least this long starts a new caption. */
  pauseMs: z.number().min(100).max(5000).default(500),
  /** Captions stay at least this long (if the next one allows it). */
  minPageMs: z.number().min(0).max(5000).default(700),
});

export const captionsElementSchema = z.object({
  ...elementBase,
  type: z.literal("captions"),
  style: captionStyleSchema.default(captionStyleSchema.parse({})),
  captionAnimation: z
    .object({
      preset: z.enum(CAPTION_ANIMATIONS).default("pop"),
      /** Length of a single word/page animation in ms. */
      duration: z.number().min(40).max(2000).default(180),
    })
    .default({ preset: "pop", duration: 180 }),
  layout: captionLayoutSchema.default(captionLayoutSchema.parse({})),
  /** Id of the preset the style came from (for the UI). */
  preset: z.string().max(40).optional(),
});

export const elementSchema = z.discriminatedUnion("type", [
  textElementSchema,
  imageElementSchema,
  shapeElementSchema,
  svgElementSchema,
  qrElementSchema,
  iconElementSchema,
  deviceElementSchema,
  pathElementSchema,
  chartElementSchema,
  groupElementSchema,
  tableElementSchema,
  captionsElementSchema,
]);

export const pageSchema = z.object({
  id,
  name: z.string().max(100).optional(),
  background: paintSchema.default("#ffffff"),
  children: z.array(id).default([]),
  /** Background colour linked to a colour token of the brand kit. */
  brand: z.object({ background: brandTokenKeySchema.optional() }).optional(),
  /** Speaker notes for presenting (not rendered). */
  notes: z.string().max(10000).optional(),
  /**
   * Own size of this page in pixels (designs without print format only; otherwise
   * the page has the size of `format`). See `scene/pages.ts`.
   */
  width: z.number().int().positive().max(20000).optional(),
  height: z.number().int().positive().max(20000).optional(),
  /** Position of the page on the board (top-left corner); without it the pages flow in a row. */
  x: z.number().min(-1_000_000).max(1_000_000).optional(),
  y: z.number().min(-1_000_000).max(1_000_000).optional(),
});

export const cmykSchema = z.tuple([
  z.number().min(0).max(100),
  z.number().min(0).max(100),
  z.number().min(0).max(100),
  z.number().min(0).max(100),
]);

/** A colour with an exact CMYK value for print (0..100 per channel). */
export const swatchSchema = z.object({
  color: colorSchema,
  cmyk: cmykSchema,
  name: z.string().max(100).optional(),
});

export const printFormatSchema = z.object({
  widthMm: z.number().positive(),
  heightMm: z.number().positive(),
  bleedMm: z.number().min(0).max(30),
  safeMm: z.number().min(0).max(50),
  /** Print shop preset, see `PRINT_PROVIDERS`. */
  provider: z.string().max(50).optional(),
  paper: z.enum(["coated", "uncoated"]).optional(),
  /** Output profile, see `PRINT_PROFILES`. */
  profile: z.string().max(50).optional(),
  /** Colours that must be printed with exactly these CMYK values. */
  swatches: z.array(swatchSchema).max(200).optional(),
});

// ---------------------------------------------------------------------------
// Timeline (video documents)
// ---------------------------------------------------------------------------

/**
 * Where the source sits in the frame. `cover` fills the frame, `contain` shows
 * everything; `zoom` scales on top of that, `x`/`y` move it from edge (-1) over
 * centre (0) to edge (1).
 */
export const clipFramingSchema = z.object({
  fit: z.enum(["cover", "contain"]).default("cover"),
  zoom: z.number().min(0.2).max(5).default(1),
  x: z.number().min(-1).max(1).default(0),
  y: z.number().min(-1).max(1).default(0),
  flipX: z.boolean().default(false),
});

/** A rectangle on the frame (document px): overlay clips, boxed main track clips. */
export const overlayBoxSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number().positive(),
  height: z.number().positive(),
});

/**
 * A clip shown inside a device mockup (screen recordings in a phone, laptop or
 * browser window, docs/19-mockups.md): the clip's box is the device's box (the
 * device keeps its aspect and sits centred in it), the picture plays on the
 * screen – `framing` places it there – and the box's corner radius is ignored.
 */
export const clipDeviceSchema = z.object({
  device: deviceIdSchema,
  finish: deviceFinishSchema.optional(),
  /** Phones and tablets turned sideways. */
  landscape: z.boolean().default(false),
  tilt: deviceTiltSchema.default({ x: 0, y: 0, z: 0 }),
  /** Soft reflection on the glass. */
  glare: z.boolean().default(false),
  url: deviceUrlSchema.optional(),
});

/**
 * Person cut out of a video clip ("Person freistellen", video/cutout.ts): the
 * worker computes a matte per video file once; the person is then drawn as
 * its own layer above the elements with `behindCutout`. "keep" leaves the
 * whole picture below as background, "remove" shows only the person (the
 * page colour or blurred copy of a main track clip, the tracks below
 * otherwise).
 */
export const clipCutoutSchema = z.object({
  background: z.enum(["keep", "remove"]).default("keep"),
});

/**
 * How a main track clip takes over from the one before it. The clips overlap
 * by the duration, so the video gets shorter by it (see `layoutClips`).
 */
export const TRANSITION_KINDS = [
  "crossfade",
  "fade-black",
  "fade-white",
  "slide-left",
  "slide-right",
  "slide-up",
  "slide-down",
  "wipe-left",
  "wipe-right",
  "zoom",
  "circle",
] as const;
export type TransitionKind = (typeof TRANSITION_KINDS)[number];

export const clipTransitionSchema = z.object({
  kind: z.enum(TRANSITION_KINDS),
  duration: z.number().min(100).max(3000).default(500),
});

/** Clip of the main track. Clips play one after another; the order is the timeline. */
export const videoClipSchema = z
  .object({
    id,
    name: z.string().max(200).optional(),
    /** Video or still image from the media library. */
    kind: z.enum(["video", "image"]).default("video"),
    assetId: z.uuid(),
    /** Part of the source that plays (images: 0 … display duration). */
    sourceIn: ms.default(0),
    sourceOut: ms,
    speed: z.number().min(0.25).max(4).default(1),
    volume: z.number().min(0).max(2).default(1),
    muted: z.boolean().default(false),
    /** Fade from / to black (picture and sound). */
    fadeIn: z.number().min(0).max(10_000).default(0),
    fadeOut: z.number().min(0).max(10_000).default(0),
    framing: clipFramingSchema.default(clipFramingSchema.parse({})),
    /** What fills the frame around the picture: page colour or a blurred copy. */
    background: z.enum(["color", "blur"]).default("color"),
    /** Transition from the previous clip into this one (ignored on the first clip). */
    transition: clipTransitionSchema.optional(),
    /**
     * Part of the frame the picture fills (split screens, cards; document px).
     * Without it the clip fills the whole frame. `framing` then places the
     * picture inside the box, around it the page colour shows.
     */
    box: overlayBoxSchema.optional(),
    /** Corner radius of `box` (document px). */
    radius: z.number().min(0).max(5000).optional(),
    color: clipColorSchema.optional(),
    /** Person cut out as its own layer (videos only). */
    cutout: clipCutoutSchema.optional(),
  })
  .refine((clip) => clip.sourceOut > clip.sourceIn, "Der Clip ist leer");

/**
 * Video or still on an additional video track above the main track (B-roll,
 * picture in picture, split screen). Freely placed in time like music; higher
 * tracks lie on top. Elements (texts, logos, captions) share the tracks
 * (video/tracks.ts), so a title can lie between two videos.
 */
export const overlayClipSchema = z
  .object({
    id,
    name: z.string().max(200).optional(),
    kind: z.enum(["video", "image"]).default("video"),
    assetId: z.uuid(),
    /** 1 = directly above the main track. Clips on the same track never overlap. */
    track: z.number().int().min(1).max(MAX_TRACKS).default(1),
    /** Position on the timeline. */
    start: ms.default(0),
    sourceIn: ms.default(0),
    sourceOut: ms,
    speed: z.number().min(0.25).max(4).default(1),
    volume: z.number().min(0).max(2).default(1),
    muted: z.boolean().default(false),
    /** Fade in / out (opacity and sound). */
    fadeIn: z.number().min(0).max(10_000).default(0),
    fadeOut: z.number().min(0).max(10_000).default(0),
    box: overlayBoxSchema,
    /** Picture inside the box, like the framing of main track clips. */
    framing: clipFramingSchema.default(clipFramingSchema.parse({})),
    opacity: z.number().min(0).max(1).default(1),
    /** Corner radius of the box (document px; ignored inside a device). */
    radius: z.number().min(0).max(5000).default(0),
    color: clipColorSchema.optional(),
    /** Shown inside a device mockup (the box is the device's). */
    device: clipDeviceSchema.optional(),
    /** Person cut out as its own layer (videos only, not in a device). */
    cutout: clipCutoutSchema.optional(),
  })
  .refine((clip) => clip.sourceOut > clip.sourceIn, "Der Clip ist leer");

/** Music or voice-over on its own track, freely placed. */
export const audioClipSchema = z
  .object({
    id,
    name: z.string().max(200).optional(),
    assetId: z.uuid(),
    /** Position on the timeline. */
    start: ms.default(0),
    sourceIn: ms.default(0),
    sourceOut: ms,
    volume: z.number().min(0).max(2).default(0.5),
    fadeIn: z.number().min(0).max(20_000).default(0),
    fadeOut: z.number().min(0).max(20_000).default(0),
    /** Turn the music down while someone speaks. */
    ducking: z.boolean().default(true),
  })
  .refine((clip) => clip.sourceOut > clip.sourceIn, "Der Clip ist leer");

/** Corrections of the automatic transcript, per word (`assetId:wordId`). */
export const wordOverrideSchema = z.object({
  text: z.string().max(200).optional(),
  hidden: z.boolean().optional(),
  /** Start a new caption at this word. */
  breakBefore: z.boolean().optional(),
  /** Keyword: shown in the emphasis style for the whole caption (video/emphasis.ts). */
  emphasis: z.boolean().optional(),
});

export const VIDEO_FPS = [24, 25, 30, 60] as const;

/**
 * A track above the main track (video/tracks.ts): entry `i` describes track
 * `i + 1`. Tracks without content disappear unless they are kept (added by
 * hand, renamed, in a group).
 */
export const trackInfoSchema = z.object({
  id,
  name: z.string().max(80).optional(),
  /** Hidden in the preview and the export. */
  hidden: z.boolean().optional(),
  /** Stays when empty. */
  keep: z.boolean().optional(),
  /** Group of tracks (always consecutive tracks). */
  group: z.string().max(100).optional(),
  /**
   * "person": the place of the cut-out person in the stack (video/tracks.ts) –
   * holds nothing; what lies on the tracks below it is behind the person.
   */
  kind: z.enum(["person"]).optional(),
});

export const trackGroupSchema = z.object({
  id,
  name: z.string().max(80),
  /** Folded in the timeline: one row for all its tracks. */
  collapsed: z.boolean().optional(),
});

export const timelineSchema = z.object({
  fps: z
    .number()
    .int()
    .refine((fps) => (VIDEO_FPS as readonly number[]).includes(fps), "Bildrate 24, 25, 30 oder 60")
    .default(30),
  clips: z.array(videoClipSchema).max(500).default([]),
  /** Clips on the video tracks above the main track (missing in older documents). */
  overlays: z.array(overlayClipSchema).max(200).optional(),
  audio: z.array(audioClipSchema).max(50).default([]),
  /** Names, visibility and groups of the tracks above the main track (missing in older documents). */
  tracks: z.array(trackInfoSchema).max(MAX_TRACKS).optional(),
  trackGroups: z.array(trackGroupSchema).max(30).optional(),
  captions: z
    .object({
      overrides: z.record(z.string().max(100), wordOverrideSchema).default({}),
      /** Words that "Schlüsselwörter automatisch hervorheben" always picks (brand, products). */
      keywords: z.array(z.string().min(1).max(60)).max(100).optional(),
    })
    .default({ overrides: {} }),
});

export const sceneDocumentSchema = z
  .object({
    schemaVersion: z.literal(SCENE_SCHEMA_VERSION),
    kind: z.enum(["design", "video"]),
    format: z.object({
      preset: z.string(),
      width: z.number().int().positive().max(20000),
      height: z.number().int().positive().max(20000),
      print: printFormatSchema.nullable().default(null),
    }),
    pages: z.array(pageSchema).min(1).max(100),
    /** Board: the editor shows all pages side by side on one canvas (designs without print format). */
    board: z.boolean().optional(),
    elements: z.record(id, elementSchema),
    /** Video documents: clips, music and caption corrections. */
    timeline: timelineSchema.optional(),
    /** Guides of the editor, the same on every page (never rendered, see `scene/layout.ts`). */
    guides: z.array(guideSchema).max(GUIDE_LIMIT).optional(),
    /** Layout grid ("Satzspiegel"): margins, columns and rows (never rendered). */
    layoutGrid: layoutGridSchema.optional(),
  })
  .superRefine((doc, ctx) => {
    if (doc.timeline) {
      const clipIds = new Set<string>();
      const tracks = [
        ["clips", doc.timeline.clips],
        ["overlays", doc.timeline.overlays ?? []],
        ["audio", doc.timeline.audio],
      ] as const;
      for (const [track, clips] of tracks) {
        for (const clip of clips) {
          if (clipIds.has(clip.id)) {
            ctx.addIssue({
              code: "custom",
              path: ["timeline", track],
              message: `Clip-ID "${clip.id}" kommt mehrfach vor`,
            });
          }
          clipIds.add(clip.id);
        }
      }
    }
    const parentOf = new Map<string, string>();
    const claim = (childId: string, owner: string, path: (string | number)[]) => {
      if (!doc.elements[childId]) {
        ctx.addIssue({ code: "custom", path, message: `Element "${childId}" existiert nicht` });
        return;
      }
      if (parentOf.has(childId)) {
        ctx.addIssue({
          code: "custom",
          path,
          message: `Element "${childId}" wird mehrfach verwendet`,
        });
        return;
      }
      parentOf.set(childId, owner);
    };
    for (const [pageIndex, page] of doc.pages.entries()) {
      for (const childId of page.children)
        claim(childId, page.id, ["pages", pageIndex, "children"]);
    }
    for (const [key, element] of Object.entries(doc.elements)) {
      if (element.id !== key) {
        ctx.addIssue({
          code: "custom",
          path: ["elements", key, "id"],
          message: "Element-ID muss dem Schlüssel entsprechen",
        });
      }
      if (element.type === "group") {
        for (const childId of element.children) {
          if (childId === key) {
            ctx.addIssue({
              code: "custom",
              path: ["elements", key, "children"],
              message: "Eine Gruppe kann sich nicht selbst enthalten",
            });
            continue;
          }
          claim(childId, key, ["elements", key, "children"]);
        }
      }
    }
    // Groups must not form cycles (a → b → a).
    for (const start of parentOf.keys()) {
      const seen = new Set<string>();
      let current: string | undefined = start;
      while (current && parentOf.has(current)) {
        if (seen.has(current)) {
          ctx.addIssue({
            code: "custom",
            path: ["elements", start],
            message: "Gruppen dürfen sich nicht gegenseitig enthalten",
          });
          break;
        }
        seen.add(current);
        current = parentOf.get(current);
      }
    }
  });

export type SceneDocument = z.infer<typeof sceneDocumentSchema>;
export type SceneElement = z.infer<typeof elementSchema>;
export type TextElement = z.infer<typeof textElementSchema>;
export type ImageElement = z.infer<typeof imageElementSchema>;
export type ShapeElement = z.infer<typeof shapeElementSchema>;
export type SvgElement = z.infer<typeof svgElementSchema>;
export type QrElement = z.infer<typeof qrElementSchema>;
export type TableElement = z.infer<typeof tableElementSchema>;
export type TableColumn = z.infer<typeof tableColumnSchema>;
export type QrLogo = z.infer<typeof qrLogoSchema>;
export type IconElement = z.infer<typeof iconElementSchema>;
export type ChartElement = z.infer<typeof chartElementSchema>;
export type ChartSeries = ChartElement["series"][number];
export type IconPath = z.infer<typeof iconPathSchema>;
export type DeviceElement = z.infer<typeof deviceElementSchema>;
export type PathElement = z.infer<typeof pathElementSchema>;
export type GroupElement = z.infer<typeof groupElementSchema>;
export type ScenePage = z.infer<typeof pageSchema>;
export type TextStyle = z.infer<typeof textStyleSchema>;
export type TextEffect = z.infer<typeof textEffectSchema>;
export type ShapePattern = z.infer<typeof shapePatternSchema>;
export type TextMark = z.infer<typeof textMarkSchema>;
export type ParagraphFormat = z.infer<typeof paragraphFormatSchema>;
export type Paint = z.infer<typeof paintSchema>;
export type Gradient = z.infer<typeof gradientSchema>;
export type GradientStop = z.infer<typeof gradientStopSchema>;
export type Stroke = z.infer<typeof strokeSchema>;
export type Shadow = z.infer<typeof shadowSchema>;
export type Crop = z.infer<typeof cropSchema>;
export type PrintFormat = z.infer<typeof printFormatSchema>;
export type Swatch = z.infer<typeof swatchSchema>;
export type ElementType = SceneElement["type"];
export type BrandBinding = z.infer<typeof brandBindingSchema>;
export type BrandBindingSlot = keyof BrandBinding;
export type CmykPercent = z.infer<typeof cmykSchema>;
export type CaptionsElement = z.infer<typeof captionsElementSchema>;
export type CaptionStyle = z.infer<typeof captionStyleSchema>;
export type CaptionLayout = z.infer<typeof captionLayoutSchema>;
export type TimeRange = z.infer<typeof timeRangeSchema>;
export type ElementAnimation = z.infer<typeof elementAnimationSchema>;
export type AnimationStep = z.infer<typeof animationStepSchema>;
export type Timeline = z.infer<typeof timelineSchema>;
export type TrackInfo = z.infer<typeof trackInfoSchema>;
export type TrackGroup = z.infer<typeof trackGroupSchema>;
export type VideoClip = z.infer<typeof videoClipSchema>;
export type OverlayClip = z.infer<typeof overlayClipSchema>;
export type OverlayBox = z.infer<typeof overlayBoxSchema>;
export type ClipTransition = z.infer<typeof clipTransitionSchema>;
export type AudioClip = z.infer<typeof audioClipSchema>;
export type ClipFraming = z.infer<typeof clipFramingSchema>;
export type ClipColor = z.infer<typeof clipColorSchema>;
export type ClipDevice = z.infer<typeof clipDeviceSchema>;
export type ClipCutout = z.infer<typeof clipCutoutSchema>;
export type WordOverride = z.infer<typeof wordOverrideSchema>;

/**
 * Media an element draws: the picture of an image or graphic, the logo of a QR
 * code, the screenshot of a device mockup.
 */
export function elementMediaId(element: SceneElement): string | null {
  if (element.type === "image" || element.type === "svg") return element.assetId;
  if (element.type === "qr") return element.logo?.assetId ?? null;
  if (element.type === "device") return element.assetId ?? null;
  return null;
}

/** All asset ids referenced by a document (used for access checks and reporting). */
export function referencedAssetIds(doc: SceneDocument): string[] {
  const ids = new Set<string>();
  for (const element of Object.values(doc.elements)) {
    const assetId = elementMediaId(element);
    if (assetId) ids.add(assetId);
  }
  for (const clip of doc.timeline?.clips ?? []) ids.add(clip.assetId);
  for (const clip of doc.timeline?.overlays ?? []) ids.add(clip.assetId);
  for (const clip of doc.timeline?.audio ?? []) ids.add(clip.assetId);
  return [...ids];
}

/** Asset ids used by elements only (images and graphics drawn by the renderer). */
export function elementAssetIds(doc: SceneDocument): string[] {
  const ids = new Set<string>();
  for (const element of Object.values(doc.elements)) {
    const assetId = elementMediaId(element);
    if (assetId) ids.add(assetId);
  }
  return [...ids];
}
